import { Model, createRecipe } from 'algu-domain'
import { P, match } from 'ts-pattern'

import { configuratorInterface } from '../configurator-interface.js'
import { ALL_VIOLATIONS } from './all-violations.js'
import { DEFAULT_LEG_TYPE_OTHER, DEFAULT_LEG_TYPE_ROUND } from './fix-table-legs-shape-update-transformer.js'

/**
 * A user should always be able to select a different material for their table top. This transformer
 * adjusts all other attributes automatically when changing material.
 */
export const fixTableTopMaterialUpdateTransformer = configuratorInterface.transformerBuilder.when
  .entity(Model.Top.types)
  .modified({ atFields: ['material'] })
  .causes.entity(Model.AnyDomainEntity.types)
  .violation(ALL_VIOLATIONS)
  .then.applySideEffect(ctx => {
    const $topEntity = ctx.getNew$Entity()
    const color = $topEntity.$availableColors.includes($topEntity.color)
      ? $topEntity.color
      : $topEntity.$availableColors.at(0)
    const edgeFinish = $topEntity.$availableEdgeFinishes.includes($topEntity.edgeFinish)
      ? $topEntity.edgeFinish
      : $topEntity.$availableEdgeFinishes.at(0)
    const fenixVariant = $topEntity.$availableFenixVariants.includes($topEntity.fenixVariant)
      ? $topEntity.fenixVariant
      : $topEntity.$availableFenixVariants.at(0)
    const thickness = $topEntity.$availableThicknesses.includes($topEntity.thickness)
      ? $topEntity.thickness
      : $topEntity.$availableThicknesses.at(0)
    const topFinish = $topEntity.$availableTopFinishes.includes($topEntity.topFinish)
      ? $topEntity.topFinish
      : $topEntity.$availableTopFinishes.at(0)

    // Round tables have different sets of diameters for different types of material
    const diameter = match($topEntity)
      .with(P.when(Model.TopCircle.is), ({ diameter, $availableDiameters }) => {
        if ($availableDiameters.includes(diameter)) {
          return diameter
        }
        return $availableDiameters.reduce((acc, val) => {
          return Math.abs(val - diameter) < Math.abs(acc - diameter) ? val : acc
        }, 0)
      })
      .otherwise(() => 1300)

    const stateDelta = Model.TopCircle.is($topEntity)
      ? {
          color,
          diameter,
          edgeFinish,
          fenixVariant,
          material: $topEntity.material,
          thickness,
          topFinish,
        }
      : {
          color,
          edgeFinish,
          fenixVariant,
          material: $topEntity.material,
          thickness,
          topFinish,
        }

    return [
      configuratorInterface.updates.DomainEntityModification({
        entityId: $topEntity.id,
        stateDelta,
        updateTag: 'fixTableTop.updateTopMaterial',
      }),
    ]
  })
  .then(
    // If the fixed table top is still not valid, we need to change the leg style as well
    configuratorInterface.transformerBuilder.when
      .entity(Model.AnyDomainEntity.types)
      .violated(ALL_VIOLATIONS)
      .then.applySideEffect(ctx => {
        const $table = ctx.getOld$Root()
        const sequence = ctx.getIdSequence()
        if (Model.TopCircle.is($table.$top)) {
          const recipe = createRecipe({ type: DEFAULT_LEG_TYPE_ROUND })
          return [
            configuratorInterface.updates.DomainEntityReplacement({
              entityId: $table.$legs.id as any,
              replacementEntity: {
                ...recipe,
                id: sequence.nextId(DEFAULT_LEG_TYPE_ROUND),
              },
              keepChildren: false,
              updateTag: 'fixTableLegs.updateTopMaterial',
            }),
          ]
        }

        const recipe = createRecipe({ type: DEFAULT_LEG_TYPE_OTHER })
        return [
          configuratorInterface.updates.DomainEntityReplacement({
            entityId: $table.$legs.id as any,
            replacementEntity: {
              ...recipe,
              id: sequence.nextId(DEFAULT_LEG_TYPE_OTHER),
            },
            keepChildren: false,
            updateTag: 'fixTableLegs.updateTopMaterial',
          }),
        ]
      })
      .build('Fix table legs for non-circle top shape update')
  )
  .build('Fix table top material update transformer')
